import { useEffect } from "react";

export type SeoMeta = {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  keywords?: string;
  /** Path of the NL canonical version, e.g. "/voor-makelaars" or "/" */
  nlPath: string;
  /** Path of the EN canonical version, e.g. "/en/for-agents" */
  enPath: string;
  /** Optional structured data (JSON-LD object or array) */
  jsonLd?: unknown;
  noindex?: boolean;
};

const SITE = "https://tribeo.co";
const DEFAULT_OG = `${SITE}/og/homepage.png`;

const upsertMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string, extra?: Record<string, string>) => {
  const sel = extra?.hreflang
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(sel);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (extra) Object.entries(extra).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.href = href;
};

const removeManaged = (sel: string) => {
  document.head.querySelectorAll(sel).forEach((n) => {
    if (n.getAttribute("data-managed") === "seo") n.remove();
  });
};

export const useSeo = (lang: "nl" | "en", meta: SeoMeta) => {
  useEffect(() => {
    const isEn = lang === "en";
    const title = meta.title;
    const description = meta.description;
    const canonicalPath = isEn ? meta.enPath : meta.nlPath;
    const canonical = `${SITE}${canonicalPath}`;

    document.title = title;
    upsertMeta('meta[name="description"]', "name", "description", description);
    if (meta.keywords) upsertMeta('meta[name="keywords"]', "name", "keywords", meta.keywords);
    upsertMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      meta.noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1",
    );

    upsertMeta('meta[property="og:type"]', "property", "og:type", "website");
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    upsertMeta('meta[property="og:title"]', "property", "og:title", meta.ogTitle ?? title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", meta.ogDescription ?? description);
    upsertMeta('meta[property="og:image"]', "property", "og:image", meta.ogImage ?? DEFAULT_OG);
    upsertMeta('meta[property="og:locale"]', "property", "og:locale", isEn ? "en_US" : "nl_NL");

    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", meta.ogTitle ?? title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", meta.ogDescription ?? description);
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", meta.ogImage ?? DEFAULT_OG);

    upsertLink("canonical", canonical);

    // hreflang
    upsertLink("alternate", `${SITE}${meta.nlPath}`, { hreflang: "nl" });
    upsertLink("alternate", `${SITE}${meta.enPath}`, { hreflang: "en" });
    upsertLink("alternate", `${SITE}${meta.nlPath}`, { hreflang: "x-default" });

    // JSON-LD (managed)
    removeManaged('script[type="application/ld+json"][data-managed="seo"]');
    if (meta.jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-managed", "seo");
      script.text = JSON.stringify(meta.jsonLd);
      document.head.appendChild(script);
    }

    document.documentElement.lang = lang;
  }, [lang, meta]);
};

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE}#org`,
  name: "Tribeo",
  legalName: "TNA B.V.",
  url: SITE,
  logo: `${SITE}/favicon.svg`,
  email: "hello@tribeo.co",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Santiagosingel 26",
    postalCode: "2548HN",
    addressLocality: "Den Haag",
    addressCountry: "NL",
  },
  contactPoint: {
    "@type": "ContactPoint",
    email: "hello@tribeo.co",
    contactType: "customer service",
  },
};

export const SOFTWARE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Tribeo",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "EUR",
    description: "Gratis voor makelaars. Leveranciers betalen 6% bij verkoop.",
  },
};

export const buildFaqSchema = (items: { q: string; a: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Tribeo",
  url: SITE,
  inLanguage: ["nl-NL", "en"],
  publisher: { "@id": `${SITE}#org` },
};

export const PRODUCT_OFFER_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Tribeo Marketplace",
  description:
    "White-label marketplace platform for real estate agents. Launch a branded marketplace, invite vendors, set your margin, earn on every order.",
  brand: { "@type": "Brand", name: "Tribeo" },
  offers: [
    {
      "@type": "Offer",
      name: "Agent plan",
      price: "0",
      priceCurrency: "EUR",
      description: "Free for real estate agents. No subscription, no setup costs.",
      availability: "https://schema.org/InStock",
    },
    {
      "@type": "Offer",
      name: "Vendor plan",
      price: "0",
      priceCurrency: "EUR",
      description:
        "Free for vendors. 6% transaction fee on a successful sale. No sale means no cost.",
      availability: "https://schema.org/InStock",
    },
  ],
};

export const buildBreadcrumbSchema = (items: { name: string; path: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE}${it.path}`,
  })),
});

export const buildServiceSchema = (params: {
  name: string;
  description: string;
  url: string;
  audience?: string;
  areaServed?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: params.name,
  description: params.description,
  url: params.url,
  provider: { "@type": "Organization", name: "Tribeo", url: SITE },
  areaServed: params.areaServed ?? "NL",
  audience: params.audience
    ? { "@type": "Audience", audienceType: params.audience }
    : undefined,
});
